import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import React, { useCallback, useMemo, useState } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import MiniDrawer from "../../Components/Drawer";
import BaseColor from "../../Config/Color";
import { FontFamily } from "../../Config/theme";
import { getApiData } from "../../Apis/apiHelper";
import BaseSetting from "../../Apis/setting";
import { toast } from "react-toastify";
import Papa from "papaparse";
import _ from "lodash";
import CModal from "../../Components/CModal";
import CInput from "../../Components/CInput";
import CButton from "../../Components/CButton";

const RAZORPAY_PAYMENTS_UNLOCK_KEY = "razorpayPaymentsUnlocked";

/** New on each full document load; unchanged during client-side routing until refresh. */
const razorpayPaymentsDocumentLoadId =
  typeof window !== "undefined"
    ? `${Date.now()}-${Math.random().toString(36).slice(2)}`
    : "";

function isRazorpayPaymentsUnlockedThisDocumentLoad() {
  if (typeof window === "undefined" || !razorpayPaymentsDocumentLoadId) return false;
  try {
    const raw = sessionStorage.getItem(RAZORPAY_PAYMENTS_UNLOCK_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.loadId === razorpayPaymentsDocumentLoadId;
  } catch {
    return false;
  }
}

function persistRazorpayPaymentsUnlock() {
  if (typeof window === "undefined" || !razorpayPaymentsDocumentLoadId) return;
  sessionStorage.setItem(
    RAZORPAY_PAYMENTS_UNLOCK_KEY,
    JSON.stringify({ loadId: razorpayPaymentsDocumentLoadId })
  );
}

function mapApiPayment(row) {
  if (!row || typeof row !== "object") return null;

  const createdDetails = row.created_at_details ?? null;
  const createdTimestampSec =
    createdDetails?.timestamp ?? row.created_at ?? row.createdAt ?? row.created ?? null;
  const createdTimestampMs =
    typeof createdTimestampSec === "number" && Number.isFinite(createdTimestampSec)
      ? createdTimestampSec * 1000
      : createdTimestampSec
        ? new Date(createdTimestampSec).getTime()
        : null;

  const createdDate =
    (typeof createdDetails?.date === "string" && createdDetails.date.trim()) || "—";
  const createdTime =
    (typeof createdDetails?.time === "string" && createdDetails.time.trim()) || "—";

  // Razorpay amount is in the smallest currency unit (e.g., INR paise)
  const amountRaw = row.amount;
  const amountNum = amountRaw != null ? Number(amountRaw) : null;
  const amount =
    amountNum != null && !Number.isNaN(amountNum) ? amountNum / 100 : 0;

  const rrn =
    row?.acquirer_data?.rrn != null && row.acquirer_data.rrn !== ""
      ? String(row.acquirer_data.rrn)
      : row?.rrn != null && row.rrn !== ""
        ? String(row.rrn)
        : "-";

  return {
    id: String(row.id ?? row.payment_id ?? ""),
    rrn,
    amount,
    createdTimestampMs:
      createdTimestampMs != null && Number.isFinite(createdTimestampMs)
        ? createdTimestampMs
        : null,
    createdDate,
    createdTime,
    email: row.email ?? "",
    contact: row.contact ?? row.phone ?? "",
    status: String(row.status ?? row.payment_status ?? "").toLowerCase() || "-",
  };
}

const fmtCell = (v) => (v === "" || v == null ? "—" : v);

function formatDateYYYYMMDD(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseYYYYMMDDToStartOfDayMs(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return null;
  const [y, m, d] = yyyyMmDd.split("-").map((n) => Number(n));
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  const ms = dt.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Rolling last 7 calendar days inclusive, ending today (same logic as initial defaults). */
function getLastSevenDaysRange() {
  const end = new Date();
  const endStr = formatDateYYYYMMDD(end);
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  start.setDate(start.getDate() - 6);
  return { start: formatDateYYYYMMDD(start), end: endStr };
}

export default function RazorpayPayments() {
  const [rows, setRows] = useState([]);
  const [todaySummary, setTodaySummary] = useState({
    today_total_payment: 0,
    today_count: 0,
    today_total_payment_without_gst: 0,
  });
  const [pageLoad, setPageLoad] = useState(true);
  const [locked, setLocked] = useState(
    () => !isRazorpayPaymentsUnlockedThisDocumentLoad()
  );
  const [authPassword, setAuthPassword] = useState("");
  const [showAuthPassword, setShowAuthPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authPasswordError, setAuthPasswordError] = useState("");
  const [dateModel, setDateModel] = useState(false);

  const defaultEnd = useMemo(() => formatDateYYYYMMDD(new Date()), []);
  const defaultStart = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    start.setDate(start.getDate() - 6); // 7 days inclusive
    return formatDateYYYYMMDD(start);
  }, []);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [dateError, setDateError] = useState("");

  // Draft dates used inside modal; active filter applies only after Done.
  const [draftStartDate, setDraftStartDate] = useState(defaultStart);
  const [draftEndDate, setDraftEndDate] = useState(defaultEnd);
  const canShowApply = Boolean(draftStartDate) && Boolean(draftEndDate);

  const validateDateRange = useCallback(
    (s = startDate, e = endDate) => {
      setDateError("");

      const startMs = parseYYYYMMDDToStartOfDayMs(s);
      const endMs = parseYYYYMMDDToStartOfDayMs(e);

      if (startMs == null || endMs == null) {
        const msg = "Start Date and End Date are required";
        setDateError(msg);
        return { ok: false, startMs, endMs, errorMsg: msg };
      }
      if (endMs < startMs) {
        const msg = "End Date cannot be before Start Date";
        setDateError(msg);
        return { ok: false, startMs, endMs, errorMsg: msg };
      }

      const days = Math.floor((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1; // inclusive
      if (days > 7) {
        const msg = "You can select maximum 7 days only";
        setDateError(msg);
        return { ok: false, startMs, endMs, errorMsg: msg };
      }

      return { ok: true, startMs, endMs, days };
    },
    [startDate, endDate]
  );

  const fetchDataWithRange = useCallback(
    async (s, e) => {
      if (locked) return;

      const vr = validateDateRange(s, e);
      if (!vr.ok) {
        toast(vr.errorMsg || "Invalid date range", { type: "warning" });
        return;
      }

      setPageLoad(true);
      try {
        const result = await getApiData(
          BaseSetting.endpoint.getAllPaymentsRazorpay,
          "post",
          { startDate: s, endDate: e, page: 1 }
        );
        if (result?.success && result?.data) {
          const raw =
            result.data.items ||
            result.data.payments ||
            result.data.list ||
            result.data.rows ||
            [];
          const mapped = _.compact((Array.isArray(raw) ? raw : []).map(mapApiPayment));
          if (mapped.length > 0) {
            setRows(mapped);
          } else {
            setRows([]);
          }
          const summary = result?.today_summary ?? result?.data?.today_summary ?? {};
          setTodaySummary({
            today_total_payment: Number(summary?.today_total_payment) || 0,
            today_count: Number(summary?.today_count) || 0,
            today_total_payment_without_gst:
              Number(summary?.today_total_payment_without_gst) || 0,
          });
        } else {
          setRows([]);
          setTodaySummary({
            today_total_payment: 0,
            today_count: 0,
            today_total_payment_without_gst: 0,
          });
        }
      } catch {
        setRows([]);
        setTodaySummary({
          today_total_payment: 0,
          today_count: 0,
          today_total_payment_without_gst: 0,
        });
      } finally {
        setPageLoad(false);
      }
    },
    [locked, validateDateRange]
  );

  const fetchData = useCallback(() => {
    return fetchDataWithRange(startDate, endDate);
  }, [fetchDataWithRange, startDate, endDate]);

  React.useEffect(() => {
    if (!locked) {
      fetchDataWithRange(startDate, endDate);
    }
    // Intentionally NOT depending on startDate/endDate: only load after unlock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, fetchDataWithRange]);

  const validateAuth = () => {
    let ok = true;
    setAuthPasswordError("");

    if (!authPassword?.trim()) {
      setAuthPasswordError("Password is required");
      ok = false;
    }
    return ok;
  };

  const unlockPage = async () => {
    if (!validateAuth()) return;
    setAuthLoading(true);
    try {
      const expected = ("N8#vQ@29Lp!" || "").trim();
      if (!expected) {
        toast("Page password is not set. Ask developer to set REACT_APP_RAZORPAY_PAYMENTS_PASSWORD.", { type: "error" });
        return;
      }
      if (authPassword === expected) {
        persistRazorpayPaymentsUnlock();
        setLocked(false);
        setAuthPassword("");
        toast("Access granted", { type: "success" });
      } else {
        toast("Invalid password", { type: "error" });
      }
    } catch (e) {
      toast(e?.toString?.() || "Auth failed", { type: "error" });
    } finally {
      setAuthLoading(false);
    }
  };

  const filteredRows = useMemo(() => rows, [rows]);

  const isDateFilterActive = useMemo(() => {
    const r = getLastSevenDaysRange();
    return startDate !== r.start || endDate !== r.end;
  }, [startDate, endDate]);

  const clearDateFilter = useCallback(() => {
    const r = getLastSevenDaysRange();
    setStartDate(r.start);
    setEndDate(r.end);
    setDraftStartDate(r.start);
    setDraftEndDate(r.end);
    setDateError("");
    fetchDataWithRange(r.start, r.end);
  }, [fetchDataWithRange]);

  const displayTodayTotal = Number(todaySummary?.today_total_payment || 0).toFixed(2);
  const displayTodayTotalWithoutGst = Number(
    todaySummary?.today_total_payment_without_gst || 0
  ).toFixed(2);
  const todayPaymentsCount = Number(todaySummary?.today_count) || 0;

  const exportCsv = () => {
    const exportRows = filteredRows.map((r) => ({
      ID: r.id,
      RRN: r.rrn,
      AMOUNT: r.amount,
      DATE: r.createdDate || "—",
      TIME: r.createdTime || "—",
      EMAIL: r.email || "-",
      CONTACT: r.contact || "-",
      STATUS: r.status,
    }));
    if (exportRows.length === 0) {
      toast("No rows to export", { type: "warning" });
      return;
    }
    const csv = Papa.unparse(exportRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "razorpay-payments.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        flex: 1.15,
        minWidth: 160,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "rrn",
        headerName: "RRN",
        flex: 0.85,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "amount",
        headerName: "Amount",
        flex: 0.65,
        minWidth: 100,
        headerAlign: "center",
        align: "center",
        type: "number",
        valueFormatter: (params) =>
          params.value != null && !Number.isNaN(Number(params.value))
            ? Number(params.value).toFixed(2)
            : "—",
      },
      {
        field: "createdTimestampMs",
        headerName: "Date",
        flex: 1.1,
        minWidth: 180,
        headerAlign: "center",
        align: "center",
        sortComparator: (v1, v2) => {
          const a = typeof v1 === "number" && Number.isFinite(v1) ? v1 : 0;
          const b = typeof v2 === "number" && Number.isFinite(v2) ? v2 : 0;
          return a - b;
        },
        renderCell: (params) => {
          const date = params?.row?.createdDate;
          const time = params?.row?.createdTime;
          return (
            <Typography variant="body2" sx={{ fontFamily: FontFamily.Regular }}>
              {fmtCell(date)} {time && time !== "—" ? `, ${time}` : ""}
            </Typography>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 160,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Typography
            noWrap
            variant="body2"
            title={params.row.email || ""}
            sx={{ fontFamily: FontFamily.Regular, maxWidth: 220 }}
          >
            {fmtCell(params.row.email)}
          </Typography>
        ),
      },
      {
        field: "contact",
        headerName: "Contact",
        flex: 0.85,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontFamily: FontFamily.Regular }}>
            {fmtCell(params.row.contact)}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.65,
        minWidth: 100,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontFamily: FontFamily.SemiBold,
              textTransform: "capitalize",
              color:
                String(params?.row?.status || "").toLowerCase() === "captured"
                  ? "success.main"
                  : String(params?.row?.status || "").toLowerCase() === "failed"
                    ? "error.main"
                    : BaseColor.textColor,
            }}
          >
            {params.row.status || "—"}
          </Typography>
        ),
      },
    ],
    []
  );

  return (
    <Grid container>
      <MiniDrawer
        header="Razorpay Payments"
        children={
          <>
            <CModal
              visible={locked}
              onClose={() => { }}
              header={false}
              style={{
                width: "100%",
                maxWidth: 520,
                margin: "0 auto",
                overflowX: "hidden",
              }}
              children={
                <Box sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: FontFamily.SemiBold,
                      color: BaseColor.textColor,
                      mb: 1,
                    }}
                  >
                    Protected Page
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: FontFamily.Regular,
                      color: BaseColor.textGray,
                      mb: 3,
                    }}
                  >
                    Enter password to view Razorpay Payments.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontFamily: FontFamily.SemiBold, mb: 1, display: "block" }}
                      >
                        Password
                      </Typography>
                      <CInput
                        placeholder="Enter password"
                        type={showAuthPassword ? "text" : "password"}
                        value={authPassword}
                        onChange={(v) => {
                          setAuthPassword(v);
                          setAuthPasswordError("");
                        }}
                        errorMsg={authPasswordError}
                        endIcon={
                          <IconButton
                            aria-label={showAuthPassword ? "Hide password" : "Show password"}
                            edge="end"
                            size="small"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setShowAuthPassword((v) => !v)}
                            tabIndex={-1}
                          >
                            {showAuthPassword ? (
                              <VisibilityOffOutlinedIcon fontSize="small" />
                            ) : (
                              <VisibilityOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <CButton
                        variant="contained"
                        onClick={unlockPage}
                        loading={authLoading}
                        style={{ width: "100%" }}
                      >
                        Unlock
                      </CButton>
                    </Grid>
                  </Grid>
                </Box>
              }
            />

            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Grid item xs={12} sm="auto">
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${BaseColor.offWhite}`,
                      borderRadius: 2,
                      px: 3,
                      py: 2,
                      bgcolor: "#fff",
                      minWidth: { sm: 260 },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: BaseColor.textGray, fontFamily: FontFamily.Regular }}
                    >
                      Today Total Amount (Without GST)
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: FontFamily.SemiBold,
                        color: BaseColor.textColor,
                        mt: 0.5,
                      }}
                    >
                      {pageLoad ? "…" : displayTodayTotalWithoutGst}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${BaseColor.offWhite}`,
                      borderRadius: 2,
                      px: 3,
                      py: 2,
                      bgcolor: "#fff",
                      minWidth: { sm: 260 },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: BaseColor.textGray, fontFamily: FontFamily.Regular }}
                    >
                      Today Total Amount
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: FontFamily.SemiBold,
                        color: BaseColor.textColor,
                        mt: 0.5,
                      }}
                    >
                      {pageLoad ? "…" : displayTodayTotal}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${BaseColor.offWhite}`,
                      borderRadius: 2,
                      px: 3,
                      py: 2,
                      bgcolor: "#fff",
                      minWidth: { sm: 260 },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: BaseColor.textGray, fontFamily: FontFamily.Regular }}
                    >
                      Payments Today
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: FontFamily.SemiBold,
                        color: BaseColor.textColor,
                        mt: 0.5,
                      }}
                    >
                      {pageLoad ? "…" : todayPaymentsCount}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
              >
                <Button
                  variant="contained"
                  onClick={() => setDateModel(true)}
                  disabled={pageLoad || locked}
                  sx={{
                    textTransform: "none",
                    fontFamily: FontFamily.Regular,
                    bgcolor: BaseColor.primary,
                    "&:hover": { bgcolor: BaseColor.disablePrimary },
                  }}
                >
                  Filter date
                </Button>
                {isDateFilterActive ? (
                  <Button
                    variant="outlined"
                    onClick={clearDateFilter}
                    disabled={pageLoad || locked}
                    sx={{
                      textTransform: "none",
                      fontFamily: FontFamily.Regular,
                      borderColor: BaseColor.textGray,
                      color: BaseColor.textColor,
                      "&:hover": {
                        borderColor: BaseColor.primary,
                        bgcolor: BaseColor.offWhite,
                      },
                    }}
                  >
                    Clear filter
                  </Button>
                ) : null}
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={exportCsv}
                  disabled={pageLoad || locked}
                  sx={{
                    textTransform: "none",
                    borderColor: BaseColor.primary,
                    color: BaseColor.primary,
                    fontFamily: FontFamily.Regular,
                    "&:hover": {
                      borderColor: BaseColor.disablePrimary,
                      bgcolor: BaseColor.offWhite,
                    },
                  }}
                >
                  Export
                </Button>
              </Grid>
            </Grid>

            <CModal
              visible={dateModel}
              onClose={() => {
                setDateModel(false);
                setDateError("");
                setDraftStartDate(startDate);
                setDraftEndDate(endDate);
              }}
              title={"Filter Date"}
              style={{ width: "100%", maxWidth: 520, margin: "0 auto", overflowX: "hidden" }}
              children={
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontFamily: FontFamily.SemiBold, mb: 1, display: "block" }}
                      >
                        Start Date
                      </Typography>
                      <CInput
                        type="date"
                        value={draftStartDate}
                        onChange={(v) => {
                          setDraftStartDate(v);
                          setDateError("");
                        }}
                        disabled={pageLoad || locked}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontFamily: FontFamily.SemiBold, mb: 1, display: "block" }}
                      >
                        End Date
                      </Typography>
                      <CInput
                        type="date"
                        value={draftEndDate}
                        onChange={(v) => {
                          setDraftEndDate(v);
                          setDateError("");
                        }}
                        disabled={pageLoad || locked}
                      />
                    </Grid>

                    {dateError ? (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          sx={{
                            width: "100%",
                            color: "error.main",
                            fontFamily: FontFamily.Regular,
                          }}
                        >
                          {dateError}
                        </Typography>
                      </Grid>
                    ) : null}

                    <Grid item xs={12}>
                      <Button
                        style={{ width: "100%" }}
                        variant="contained"
                        disabled={!canShowApply || pageLoad || locked}
                        onClick={async () => {
                          const vr = validateDateRange(draftStartDate, draftEndDate);
                          if (!vr.ok) {
                            toast(vr.errorMsg || "Invalid date range", { type: "warning" });
                            return;
                          }

                          setStartDate(draftStartDate);
                          setEndDate(draftEndDate);
                          await fetchDataWithRange(draftStartDate, draftEndDate);
                          setDateModel(false);
                        }}
                        sx={{
                          textTransform: "none",
                          fontFamily: FontFamily.Regular,
                          bgcolor: BaseColor.primary,
                          "&:hover": { bgcolor: BaseColor.disablePrimary },
                        }}
                      >
                        Done
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              }
            />

            <Grid container mt={3}>
              {locked ? (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  sx={{ width: "100%", minHeight: 400 }}
                >
                  <Typography sx={{ fontFamily: FontFamily.Regular, color: BaseColor.textGray }}>
                    This page is locked.
                  </Typography>
                </Grid>
              ) : pageLoad ? (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  sx={{ width: "100%", minHeight: 400 }}
                >
                  <CircularProgress sx={{ color: BaseColor.primary }} />
                </Grid>
              ) : (
                <Box sx={{ width: "100%" }}>
                  <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    disableColumnFilter
                    disableColumnSelector
                    disableRowSelectionOnClick
                    autoHeight
                    getRowHeight={() => "auto"}
                    sx={{
                      width: "100%",
                      "& .MuiDataGrid-cell": { alignContent: "center" },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: BaseColor.offWhite,
                        fontFamily: FontFamily.SemiBold,
                      },
                    }}
                    slots={{
                      toolbar: function CustomToolbar() {
                        return (
                          <GridToolbarContainer>
                            <GridToolbarQuickFilter />
                            <Box sx={{ flexGrow: 1 }} />
                            <Tooltip title="Search / Refresh">
                              <span>
                                <IconButton
                                  onClick={fetchData}
                                  disabled={pageLoad || locked}
                                  size="small"
                                >
                                  <RefreshOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </GridToolbarContainer>
                        );
                      },
                    }}
                    slotProps={{
                      toolbar: {
                        printOptions: { disableToolbarButton: true },
                        csvOptions: { disableToolbarButton: true },
                      },
                    }}
                    initialState={{
                      sorting: {
                        sortModel: [{ field: "createdTimestampMs", sort: "desc" }],
                      },
                      pagination: { paginationModel: { pageSize: 25 } },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                  />
                </Box>
              )}
            </Grid>
          </>
        }
      />
    </Grid>
  );
}
